import { cloneDeep, mergeWith } from 'lodash'
import { StateCreator } from 'zustand'

import { TAgent } from '~/features/agents/models'
import { getDefaultAgentData } from '~/features/agents/utils'
import { TChatMessage } from '~/features/chatbot/models'
import { defaultChatBotLayout } from '~/features/settings/components/chatBot/utils'
import { TChatbotSettingsLayout } from '~/features/settings/models'
import EStoreStateKeys from '~/models/store'

type TChatbotDefinition = {
  // todo: see maybe we can simplify this, or use the agent from query directly.
  agent: Partial<TAgent>
  layout: TChatbotSettingsLayout
  conversationId: string
  showBookAMeeting: boolean
  messages: TChatMessage[]
  isMinimized: boolean
  // Set once by useEmbeddedChatContext (called from ChatBotLayout, which stays
  // mounted across LinkView<->Simple theme switches and Simple's minimize/
  // reopen). Living in the store rather than component state is what makes
  // these survive those transitions instead of being lost/reset.
  referrer: string
  currentPage: string
}

type TChatbot = {
  [EStoreStateKeys.chatbot]: TChatbotDefinition
}

export type TChatbotState = TChatbot & {
  setChatbot(chatbot: Partial<TChatbotDefinition>): void
  resetChatbot(): void
}

export const initialChatbotState: TChatbotDefinition = {
  agent: getDefaultAgentData(),
  layout: cloneDeep(defaultChatBotLayout),
  conversationId: '',
  showBookAMeeting: false,
  messages: [],
  isMinimized: false,
  referrer: '',
  currentPage: '',
}

const createChatbotSlice: StateCreator<
  TChatbotState,
  [['zustand/devtools', unknown]],
  []
> = (set) => ({
  chatbot: cloneDeep(initialChatbotState),
  // Plain lodash `merge` updates array fields (messages) by INDEX rather
  // than replacing them — passing a shorter array (e.g. an empty transcript
  // right after a restart, or a short resumed history) left the OLD array's
  // trailing elements on screen, since merge only ever overwrites indices
  // present in the new value (Cursor-flagged, High severity: every prior
  // caller happened to always pass a same-or-longer array — mutate()/
  // finalizeAssistantMessage both build on `[...chatbot.messages, x]` — so
  // this never surfaced until useChatV2's SESSION_READY handling became the
  // first caller to legitimately need to REPLACE the list with a shorter
  // one). `mergeWith`'s customizer makes any array field a full replace
  // instead of an index-merge; every other (non-array) field still deep-
  // merges exactly as before.
  setChatbot: (chatbot) =>
    set(
      (state) => ({
        chatbot: cloneDeep(
          mergeWith(state.chatbot, chatbot, (_target, source) =>
            Array.isArray(source) ? source : undefined,
          ),
        ),
      }),
      undefined,
      {
        type: 'setChatbot',
        chatbot,
      },
    ),
  resetChatbot: () =>
    set(() => ({ chatbot: cloneDeep(initialChatbotState) }), undefined, {
      type: 'resetChatbot',
    }),
})

export default createChatbotSlice
